import React from 'react';
import { motion } from "framer-motion";
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const PrivacyPolicy = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-background text-foreground overflow-x-hidden relative">
        {/* Animated Background Blobs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <motion.div
            animate={{ x: [0, 80, 0], y: [0, 50, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute top-[15%] -left-[5%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[100px]"
          />
          <motion.div
            animate={{ x: [0, -60, 0], y: [0, 100, 0] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-[10%] -right-[5%] w-[35%] h-[35%] bg-accent/5 rounded-full blur-[100px]"
          />
        </div>

        <section id="privacy" className="relative z-10 pt-32 pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              {/* Header */}
              <div className="text-center mb-16 space-y-4">
                <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight">
                  Privacy <span className="text-primary">Policy</span>
                </h1>
                <p className="text-muted-foreground font-medium">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-card rounded-2xl shadow-lg p-8 md:p-12 space-y-8 text-foreground border border-border">
              
              {/* Platform Info */}
              <div className="p-4 bg-background border border-border rounded-lg mb-8">
                <p className="text-muted-foreground"><strong>Platform:</strong> Quro (Website & Mobile Application)</p>
                <p className="text-muted-foreground"><strong>Operated By:</strong> Quick Retail Online Private Limited ("Quro", "We", "Us", "Our")</p>
                <p className="text-muted-foreground text-sm mt-1"><strong>FSSAI Reg. No.:</strong> 30260424124243466</p>
              </div>

              {/* Introduction */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">1. Introduction</h2>
                <p className="leading-relaxed text-muted-foreground">
                  At Quro ("we," "our," or "us"), we are committed to protecting your privacy. This Privacy Policy explains how 
                  we collect, use, disclose, and safeguard your information when you use our platform, including our website, 
                  mobile application, and services (collectively, the "Service").
                </p>
                <p className="leading-relaxed mt-4 text-muted-foreground">
                  Please read this Privacy Policy carefully. By using our Service, you agree to the collection and use of 
                  information in accordance with this policy. If you do not agree with our policies and practices, please do 
                  not use our Service.
                </p>
              </div>

              {/* Information We Collect */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold mt-6 mb-3 font-display">2.1 Information You Provide</h3>
                <p className="leading-relaxed mb-4 text-muted-foreground">We collect information that you provide directly to us, including:</p>
                <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                  <li><strong>Account Information:</strong> Name, email address, phone number, password, and profile information</li>
                  <li><strong>Transaction Information:</strong> Purchase history, payment information, billing and shipping addresses</li>
                  <li><strong>Communication Data:</strong> Messages, feedback, and correspondence with us or other users</li>
                  <li><strong>Content:</strong> Reviews, ratings, photos, and other content you submit</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 font-display">2.2 Automatically Collected Information</h3>
                <p className="leading-relaxed mb-4 text-muted-foreground">When you use our Service, we automatically collect certain information, including:</p>
                <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                  <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, mobile network information</li>
                  <li><strong>Usage Data:</strong> Pages visited, features used, time spent, click patterns, and navigation paths</li>
                  <li><strong>Location Data:</strong> GPS coordinates, IP address, and location-based information (with your permission)</li>
                  <li><strong>Log Data:</strong> IP address, browser type, access times, and referring website addresses</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 font-display">2.3 Information from Third Parties</h3>
                <p className="leading-relaxed text-muted-foreground">
                  We may receive information about you from third parties, such as payment processors, social media platforms 
                  (if you connect your account), and service providers who help us verify your identity or prevent fraud.
                </p>
              </div>

              {/* How We Use Your Information */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">3. How We Use Your Information</h2>
                <p className="leading-relaxed mb-4 text-muted-foreground">We use the information we collect to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                  <li>Provide, maintain, and improve our Service</li>
                  <li>Process transactions and send related information</li>
                  <li>Send you technical notices, updates, and support messages</li>
                  <li>Respond to your comments, questions, and requests</li>
                  <li>Personalize your experience and provide recommendations</li>
                  <li>Monitor and analyze usage patterns and trends</li>
                  <li>Detect, prevent, and address technical issues and fraudulent activity</li>
                  <li>Send you marketing communications (with your consent)</li>
                  <li>Comply with legal obligations and enforce our terms</li>
                </ul>
              </div>

              {/* How We Share Your Information */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">4. How We Share Your Information</h2>
                <p className="leading-relaxed mb-4 text-muted-foreground">We may share your information in the following circumstances:</p>
                
                <h3 className="text-xl font-semibold mt-6 mb-3 font-display">4.1 With Sellers and Vendors</h3>
                <p className="leading-relaxed text-muted-foreground">
                  We share necessary information with sellers to facilitate transactions, including your name, contact information, 
                  delivery address, and order details.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 font-display">4.2 With Service Providers</h3>
                <p className="leading-relaxed text-muted-foreground">
                  We may share information with third-party service providers who perform services on our behalf, such as payment 
                  processing, data analysis, email delivery, hosting, and customer service.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 font-display">4.3 For Legal Reasons</h3>
                <p className="leading-relaxed text-muted-foreground">
                  We may disclose information if required by law, court order, or government regulation, or if we believe disclosure 
                  is necessary to protect our rights, property, or safety, or that of our users or others.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 font-display">4.4 Business Transfers</h3>
                <p className="leading-relaxed text-muted-foreground">
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that 
                  transaction.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 font-display">4.5 With Your Consent</h3>
                <p className="leading-relaxed text-muted-foreground">
                  We may share your information with your explicit consent or at your direction.
                </p>
              </div>

              {/* Data Security */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">5. Data Security</h2>
                <p className="leading-relaxed text-muted-foreground">
                  We implement appropriate technical and organizational measures to protect your personal information against 
                  unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet 
                  or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your 
                  information, we cannot guarantee absolute security.
                </p>
              </div>

              {/* Your Rights */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">6. Your Rights and Choices</h2>
                <p className="leading-relaxed mb-4 text-muted-foreground">You have certain rights regarding your personal information:</p>
                <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                  <li><strong>Access:</strong> Request access to your personal information</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                  <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                  <li><strong>Objection:</strong> Object to processing of your personal information</li>
                  <li><strong>Withdrawal of Consent:</strong> Withdraw consent where processing is based on consent</li>
                </ul>
                <p className="leading-relaxed mt-4 text-muted-foreground">
                  To exercise these rights, please contact us at <a href="mailto:q@quro.today" className="text-primary hover:text-primary/80 transition-colors">q@quro.today</a>.
                </p>
              </div>

              {/* Cookies and Tracking */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">7. Cookies and Tracking Technologies</h2>
                <p className="leading-relaxed text-muted-foreground">
                  We use cookies, web beacons, and similar tracking technologies to collect and store information about your 
                  preferences and activity on our Service. You can control cookies through your browser settings, but disabling 
                  cookies may limit your ability to use certain features of our Service.
                </p>
              </div>

              {/* Data Retention */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">8. Data Retention</h2>
                <p className="leading-relaxed text-muted-foreground">
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy 
                  Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, 
                  we will securely delete or anonymize it.
                </p>
              </div>

              {/* Children's Privacy */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">9. Children's Privacy</h2>
                <p className="leading-relaxed text-muted-foreground">
                  Our Service is not intended for children under the age of 18. We do not knowingly collect personal information 
                  from children. If you are a parent or guardian and believe your child has provided us with personal information, 
                  please contact us immediately.
                </p>
              </div>

              {/* International Transfers */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">10. International Data Transfers</h2>
                <p className="leading-relaxed text-muted-foreground">
                  Your information may be transferred to and processed in countries other than your country of residence. These 
                  countries may have data protection laws that differ from those in your country. By using our Service, you consent 
                  to the transfer of your information to these countries.
                </p>
              </div>

              {/* Changes to Privacy Policy */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">11. Changes to This Privacy Policy</h2>
                <p className="leading-relaxed text-muted-foreground">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy 
                  Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically 
                  for any changes.
                </p>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold mb-4 font-display">12. Contact Us</h2>
                <p className="leading-relaxed mb-4 text-muted-foreground">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border">
                  <p className="font-semibold text-foreground">Quro</p>
                  <p className="text-muted-foreground">Email: <a href="mailto:q@quro.today" className="text-primary hover:text-primary/80 transition-colors">q@quro.today</a></p>
                  <div className="text-muted-foreground mt-4 space-y-1">
                    <p className="font-semibold text-foreground">Registered Office:</p>
                    <p>Quick Retail Online Private Limited</p>
                    <p>24-21, Ashoknagar</p>
                    <p>Ramachandra Puram, Hyderabad-502032</p>
                  </div>
                  <div className="text-muted-foreground mt-4 space-y-1">
                    <p className="font-semibold text-foreground">HQ Office:</p>
                    <p>Suite 406, 4th floor, MyScape Weave</p>
                    <p>Financial District, Nankramaguda</p>
                    <p>Hyderabad-500032</p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Back to Top Button */}
          <div className="mt-12 text-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 font-semibold transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              <span>Back to Top</span>
            </button>
          </div>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;
